import { createColumnHelper } from "@tanstack/react-table";
import GenericTable, { GenericTableProps } from "components/Table/GenericTable";
import { ResponseModel } from "services/ApiClient/responseModel";
import apiClient from "services/ApiClient";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { RandomEmptyIssueState } from "components/EmptyComponents/RandomEmptyState";
import { ActionTypeTag } from "design-system";
import { InternalLink } from "components/Link";
import { Box, Flex } from "@chakra-ui/react";
import SearchBar from "components/SearchBar";
import { useMemo, useState } from "react";
import { ShowIssuesOrReadyByIdTag } from "features/Issues/Components/NumberOfIssuesTag";
import { NewHireJourneyId } from "models/newHire";
import ActionRowResolutionButtons from "features/Issues/Components/ActionRowResolutionButtons";
import { useNewHiresWithIssuesListQuery } from "features/Issues/hooks";
import useSearchParam from "hooks/useSearchParam";
import NewHireJourneySelect from "components/Form/Select/NewHireJourneySingleSelect";
import { Text12 } from "components/Text";
import { ActionV2, ActionSummary } from "features/Resources/Shared/models";
import { isActionResolved } from "features/Resources/Shared/helpers";
import { actionPathByPrefixId } from "helpers/url/pages/v2/actionPaths";
import { DisplayRecipientsColumn } from "features/Resources/Shared/Components/DisplayRecipients";
import { sharedResourceKeys } from "features/Resources/Shared/hooks";
import { generatePath } from "react-router";
import PrimarySendAtValue from "features/Resources/Shared/Components/PrimarySendAtValue";

const columnHelper = createColumnHelper<ActionSummary>();

const createColumnDefs = () => [
  columnHelper.accessor((row) => row, {
    id: "triggerAt",
    header: "Scheduled",
    cell: (row) => <PrimarySendAtValue action={row.getValue()} size="sm" />,
    minSize: 79,
    maxSize: 79,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row, {
    id: "actionType",
    header: "Type",
    cell: (row) => {
      const summary = row.getValue();
      const meetingType = summary?.actionType === "meeting" ? (summary).action?.meetingType : undefined;
      return <ActionTypeTag.IconBadgeWithMeetingType actionType={summary?.actionType} meetingType={meetingType} />;
    },
    minSize: 24,
    maxSize: 24,
    size: 24,
    meta: {
      sx: {
        paddingLeft: 2,
        paddingRight: 0,
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    cell: (row) => (
      <Box width="fit-content">
        <InternalLink
          to={actionPathByPrefixId(row.getValue()?.action?.prefixId)}
          textDecoration={isActionResolved(row.getValue()?.action) ? "line-through" : "none"}
          width="fit-content"
        >
          <Text12 wordBreak="break-word">
            {row.getValue()?.action?.name}
          </Text12>
        </InternalLink>
      </Box>
    ),
    maxSize: 200,
    minSize: 200,
    size: 200,
    enableSorting: false,
    meta: {
      sx: {
        paddingLeft: 0,
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "target",
    header: "To",
    cell: (row) => <DisplayRecipientsColumn action={row.getValue()} />,
    minSize: 28,
    maxSize: 28,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row, {
    id: "issuesCount",
    header: "",
    cell: (row) => (
      <Flex width="fit-content" justify="flex-start">
        <ShowIssuesOrReadyByIdTag action={row.getValue()?.action} />
      </Flex>
    ),
    minSize: 99,
    maxSize: 99,
    meta: { headerHStackProps: { justifyContent: "center" } },
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row, {
    id: "moreOptions",
    header: "",
    enableSorting: false,
    cell: (row) => (
      <ActionRowResolutionButtons action={row.getValue()?.action} />
    ),
  }),
];

export function ErroredActionsByNewHireTable({ newHireId }: { newHireId: NewHireJourneyId }) {
  const [searchText, setSearchText] = useState("");

  const additionalQueries = useMemo(() => {
    const queries: Record<string, string> = {
      withActiveIssues: "true",
      newHireJourneyId: newHireId,
    };
    if (searchText && searchText.length > 2) {
      queries.q = searchText;
    }
    return queries;
  }, [newHireId, searchText]);
  const tableProps: GenericTableProps<ActionV2> = {
    fetchData: (query, signal) => apiClient
      .performRequest<ResponseModel<ActionV2[]>>(`${generatePath(API_ROUTES.activityFeed.base)}${query}`, undefined, signal)
      .then((response) => response.payload),
    queryKey: (queryString: string) => (queryString ? sharedResourceKeys.list(queryString) : sharedResourceKeys.lists()),
    additionalQueries,
    columns: createColumnDefs(),
    meta: {
      pagination: { perPage: 20, initialPage: 1 },
    },
    textWrap: true,
    emptyStateComponent: <RandomEmptyIssueState />,
    tableLayout: "fixed",
  };

  return (
    <>
      <Flex justify="space-between">
        <SearchBar
          minWidth="500"
          onInput={setSearchText}
          my="4"
        />
      </Flex>
      <Box overflowX="auto" width="100%" mt="2">
        <GenericTable
          mt="4"
          {...tableProps}
        />
      </Box>
    </>
  );
}

export function ErroredActionsWithIssuesTable() {
  const [searchText, setSearchText] = useState("");
  const { value: selectedNewHireId, setSearchParam: setSelectedNewHireId } = useSearchParam("newHireId");

  const { data: initialNewHireJourneys } = useNewHiresWithIssuesListQuery();

  const additionalQueries = useMemo(() => {
    const queries: Record<string, string> = {
      withActiveIssues: "true",
    };
    if (searchText && searchText.length > 2) {
      queries.q = searchText;
    }
    if (selectedNewHireId) {
      queries.newHireJourneyId = selectedNewHireId;
    }
    return queries;
  }, [searchText, selectedNewHireId]);
  const tableProps: GenericTableProps<ActionV2> = {
    fetchData: (query, signal) => apiClient
      .performRequest<ResponseModel<ActionV2[]>>(`${generatePath(API_ROUTES.activityFeed.base)}${query}`, undefined, signal)
      .then((response) => response.payload),
    queryKey: (queryString: string) => (queryString ? sharedResourceKeys.list(queryString) : sharedResourceKeys.lists()),
    additionalQueries,
    columns: createColumnDefs(),
    meta: {
      pagination: { perPage: 20, initialPage: 1 },
    },
    textWrap: true,
    emptyStateComponent: <RandomEmptyIssueState />,
    tableLayout: "fixed",
  };

  return (
    <>
      <Flex justify="space-between">
        <SearchBar
          minWidth="500"
          name="actionsSearch"
          onInput={setSearchText}
          my="4"
        />
        <Flex minWidth="250px" maxWidth="250px">
          <NewHireJourneySelect
            newHireJourneys={initialNewHireJourneys}
            selectedNewHireJourneyId={selectedNewHireId}
            onChange={(e) => setSelectedNewHireId(e as string)}
            placeholder="Filter by New Hire"
            isClearable
            useBasicStyles
          />
        </Flex>
      </Flex>
      <GenericTable
        {...tableProps}
      />
    </>
  );
}
